import { useCallback, useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, X, Search, Star } from 'lucide-react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { Pagination } from '../../components/ui/Pagination'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

type ProductRow = {
  id: number
  name: string
  stock: number
  threshold: number
  status: string
  avgRating: number
  ratingCount: number
  images: { url: string }[]
  weightOptions: { price: number }[]
}

export function ProductListPage() {
  const navigate = useNavigate()
  const [products, setProducts] = useState<ProductRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('All')
  const [sort, setSort] = useState('newest')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (status !== 'All') params.set('status', status)
      params.set('sort', sort)
      params.set('page', String(page))
      params.set('limit', String(limit))
      const { data } = await api.get(`/admin/products?${params}`)
      setProducts(data.products)
      setTotal(data.total)
    } finally {
      setLoading(false)
    }
  }, [search, status, sort, page, limit])

  useEffect(() => { void fetchProducts() }, [fetchProducts])
  useEffect(() => { setPage(1) }, [search, status, sort, limit])
  useEffect(() => {
    const handleRefresh = () => {
      void fetchProducts()
    }

    window.addEventListener('seasonly-admin-products-refresh', handleRefresh)
    return () => window.removeEventListener('seasonly-admin-products-refresh', handleRefresh)
  }, [fetchProducts])

  const handleDelete = async () => {
    if (!confirmId) return
    setDeletingId(confirmId)
    setConfirmId(null)
    try {
      await api.delete(`/admin/products/${confirmId}`)
      setProducts(prev => prev.filter(p => p.id !== confirmId))
      setTotal(prev => prev - 1)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title={`Products (${total})`}
        subtitle="Manage your mango catalog — add, edit, publish or delete products."
        action={<NavLink to="/products/new"><Button icon={Plus}>Add Product</Button></NavLink>}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            className="w-full rounded-lg border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
            placeholder="Search by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <select className="rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-mango-orange" value={sort} onChange={e => setSort(e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="stock_asc">Stock: Low to High</option>
        </select>
      </div>

      <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-16 text-center text-stone-400 text-sm">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center text-stone-400 text-sm">No products found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                  <th className="px-4 py-3">Image</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Price (from)</th>
                  <th className="px-4 py-3">Stock</th>
                  <th className="px-4 py-3">Rating</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {products.map(p => (
                  <tr key={p.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      {p.images[0] ? (
                        <img src={p.images[0].url} alt={p.name} className="h-12 w-12 rounded-lg object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-mango-yellow/30 flex items-center justify-center text-xs text-stone-400">No img</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-leaf-dark">{p.name}</td>
                    <td className="px-4 py-3 font-semibold">{p.weightOptions[0] ? `₹${p.weightOptions[0].price.toLocaleString('en-IN')}` : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{p.stock}</div>
                      {p.stock <= 0 ? (
                        <div className="mt-1 text-xs font-semibold text-red-600">Out of stock</div>
                      ) : p.threshold > 0 && p.stock <= p.threshold ? (
                        <div className="mt-1 text-xs font-semibold text-amber-600">Limited stock</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {p.ratingCount > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star size={13} className="fill-amber-400 stroke-amber-400" />
                          <span className="text-sm font-semibold text-stone-700">{p.avgRating.toFixed(1)}</span>
                          <span className="text-xs text-stone-400">({p.ratingCount})</span>
                        </div>
                      ) : (
                        <span className="text-xs text-stone-400">No ratings</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusPill value={p.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 transition-colors" onClick={() => navigate(`/products/${p.id}`)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button className="rounded-lg p-2 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40" onClick={() => setConfirmId(p.id)} disabled={deletingId === p.id} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pagination
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          page={page}
          total={total}
        />
      </section>

      {confirmId !== null && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-stone-950/40 p-4">
          <div className="w-full max-w-sm rounded-xl border border-stone-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-stone-950">Delete product?</h3>
                <p className="mt-1 text-sm leading-6 text-stone-600">This action cannot be undone.</p>
              </div>
              <button className="rounded-lg p-1 text-stone-400 hover:bg-stone-100" onClick={() => setConfirmId(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setConfirmId(null)} variant="ghost">Cancel</Button>
              <Button onClick={handleDelete} variant="danger">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
